import { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { ref, onValue, query, orderByChild, limitToLast } from 'firebase/database';
import { db } from '../firebase';
import { useConfig } from '../context/ConfigContext';
import { ADMIN_COLORS, OPEN_PLAY_RECENT_JOINS_LIMIT } from '../constants/bookingConst';
import { setOpenPlayJoinStatus } from '../utils/openPlayUtil';
import { spinnerRing, formatTimeSlotRange } from '../utils/bookingUtils';
import { hasReceipt, getOpenPlayReceipt } from '../utils/receiptUtil';
import ConnectionErrorScreen from './ConnectionErrorScreen';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import '../css/AdminOpenPlay.css';
import { formatMoney } from '../utils/currencyUtil';

const COLORS = ADMIN_COLORS;

const TABS = [
  { id: 'pending', label: 'Pending Review' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'declined', label: 'Declined' },
];

// A join only shows up for admin review once the guest has uploaded a
// receipt (status flips 'pending_payment' -> 'pending' in
// uploadOpenPlayScreenshot). Unpaid 'pending_payment' joins are left out of
// every tab — they're abandoned carts, not something the admin needs to act on.
const REVIEW_STATUSES = { pending: 'pending', confirmed: 'confirmed', declined: 'declined' };

/**
 * Admin "Open Play" screen — review, confirm, or decline guest Open Play
 * join requests (Config -> Sports & Courts -> Open Play toggle controls
 * whether guests can submit these at all; see OpenPlayModal.jsx for the
 * guest-facing flow that creates these records under `openPlayJoins`).
 */

function AdminOpenPlay() {
  const { config } = useConfig();
  const [joins, setJoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [actioningId, setActioningId] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  // Which join's receipt is currently being fetched. The image lives at
  // openPlayReceipts/{joinId} rather than on the join record (see
  // receiptUtil.js), so this list no longer downloads every receipt just to
  // render a row — each one is read when its own button is pressed.
  const [loadingReceiptId, setLoadingReceiptId] = useState(null);
  // Bumped by Try Again to re-open the subscription below — onValue on an
  // unreachable database never calls back at all, so waiting longer achieves
  // nothing and only a fresh subscription is a real retry.
  const [reloadKey, setReloadKey] = useState(0);
  const { timedOut, restart: restartTimeout } = useLoadingTimeout(loading);

  /**
   * Try Again on the timeout screen — restarts the countdown and
   * re-subscribes to the joins query.
   *
   * @returns {void}
   */
  const handleRetry = () => {
    restartTimeout();
    setReloadKey((key) => key + 1);
  };

  /**
   * Fetches one join's payment receipt and opens it fullscreen.
   *
   * @param {object} join - the join record, carrying its Firebase `id`.
   * @returns {Promise<void>}
   */
  const handleViewReceipt = async (join) => {
    setLoadingReceiptId(join.id);
    try {
      const image = await getOpenPlayReceipt(join.id, join);
      if (image) setFullscreenImage(image);
    } catch (error) {
      console.error('Error loading payment receipt:', error);
    } finally {
      setLoadingReceiptId(null);
    }
  };

  useEffect(() => {
    if (!db) return undefined;
    // Bounded to the most recent OPEN_PLAY_RECENT_JOINS_LIMIT records. This used to
    // subscribe to the entire openPlayJoins node — every join ever made,
    // each carrying its payment receipt — and filter to the active tab in
    // JS. createdAt is written as an ISO string everywhere (openPlayUtil.js),
    // so ordering on it is chronological and limitToLast takes the newest.
    // Requires .indexOn: ["createdAt"] on /openPlayJoins.
    const joinsQuery = query(
      ref(db, 'openPlayJoins'),
      orderByChild('createdAt'),
      limitToLast(OPEN_PLAY_RECENT_JOINS_LIMIT),
    );
    const unsub = onValue(joinsQuery, (snapshot) => {
      const list = [];
      if (snapshot.exists()) {
        // Statement body, not a concise one: Firebase's DataSnapshot.forEach
        // stops enumerating as soon as the callback returns a TRUTHY value,
        // and `list.push()` returns the new array length — so
        // `child => list.push(...)` returns 1 on the first child and this
        // screen would show exactly one join no matter how many exist.
        snapshot.forEach((child) => { list.push({ id: child.key, ...child.val() }); });
      }
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setJoins(list);
      setLoading(false);
    }, (error) => {
      console.error('Open Play listener failed:', error);
      setLoading(false);
    });
    return () => unsub();
  }, [reloadKey]);

  const sportName = (sportId) => (config?.sports || []).find(s => s.id === sportId)?.name || sportId;

  const visibleJoins = useMemo(
    () => joins.filter(j => j.status === REVIEW_STATUSES[activeTab]),
    [joins, activeTab]
  );

  const pendingCount = useMemo(() => joins.filter(j => j.status === 'pending').length, [joins]);

  const handleAction = async (joinId, status) => {
    setActioningId(joinId);
    try {
      await setOpenPlayJoinStatus(joinId, status);
    } catch (error) {
      console.error('Failed to update Open Play join:', error);
    } finally {
      setActioningId(null);
    }
  };

  // Ran past the configured timeout — the requests are not coming, so give
  // the admin an explanation and a retry instead of an endless spinner. Not
  // also gated on `loading`: a query that lands late must not replace the
  // error with a list the admin may already have stopped trusting.
  if (timedOut) {
    return (
      <div className="admin-openplay">
        <ConnectionErrorScreen onRetry={handleRetry} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-openplay" style={{ padding: '3rem 0', textAlign: 'center' }}>
        {spinnerRing('Loading Open Play requests…')}
      </div>
    );
  }

  return (
    <div className="admin-openplay">
      <h2 className="section-title" style={{ color: COLORS.ink }}>Open Play</h2>
      <p style={{ opacity: 0.65, fontSize: '0.82rem', margin: '0 0 1rem', color: COLORS.textSecondary }}>
        Guests who submit a payment receipt through the "Join Open Play" flow show up here for review.
      </p>

      <div className="openplay-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`openplay-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              color: activeTab === tab.id ? '#fff' : COLORS.ink,
              background: activeTab === tab.id ? COLORS.cyanDeep : 'transparent',
              borderColor: COLORS.line,
            }}
          >
            {tab.label}
            {tab.id === 'pending' && pendingCount > 0 && (
              <span className="openplay-tab-badge">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {visibleJoins.length === 0 ? (
        <div className="openplay-empty" style={{ borderColor: COLORS.line, color: COLORS.textSecondary }}>
          No {TABS.find(t => t.id === activeTab)?.label.toLowerCase()} requests right now.
        </div>
      ) : (
        <div className="openplay-cards">
          {visibleJoins.map(join => (
            <div key={join.id} className="openplay-card" style={{ borderColor: COLORS.line }}>
              <div className="openplay-card-head">
                <div>
                  <div className="openplay-card-sport">{sportName(join.sportId)} Open Play</div>
                  <div className="openplay-card-date" style={{ color: COLORS.textSecondary }}>
                    {join.date ? format(parseISO(join.date), 'EEEE, MMMM d, yyyy') : ''}
                    {join.timeSlot ? ` · ${formatTimeSlotRange(join.timeSlot)}` : ''}
                  </div>
                </div>
                <div className="openplay-card-amount" style={{ color: COLORS.ink }}>
                  {formatMoney(Number(join.totalAmount || 0), config)}
                </div>
              </div>

              <div className="openplay-card-body">
                <div><strong>{join.fullName}</strong> <span style={{ color: COLORS.inkFaint }}>· booker</span></div>
                <div style={{ color: COLORS.textSecondary }}>{join.phone}{join.email ? ` · ${join.email}` : ''}</div>
                <div style={{ color: COLORS.textSecondary }}>
                  {join.numPlayers} player{Number(join.numPlayers) > 1 ? 's' : ''} &times; {formatMoney(Number(join.pricePerPlayer || 0), config)}
                </div>

                {/* Everyone the request covers, by name. Only the booker used
                    to appear, so "3 players" meant three anonymous bodies —
                    nobody could be checked in off this screen, and an admin
                    approving it had no idea who was actually coming. A join
                    saved before rosters were stored falls back to the booker. */}
                <div className="openplay-card-players">
                  <div className="openplay-card-players-label" style={{ color: COLORS.inkFaint }}>Players</div>
                  {(join.players?.length ? join.players : [{ name: join.fullName, phone: join.phone }])
                    .map((player, index) => (
                      // Names repeat within a family group, so the key is the
                      // position — this list is never reordered.
                      <div className="openplay-card-player" key={index}>
                        <span className="openplay-card-player-num" style={{ color: COLORS.inkFaint }}>{index + 1}</span>
                        <span className="openplay-card-player-name">{player.name}</span>
                        {player.phone && (
                          <span className="openplay-card-player-phone" style={{ color: COLORS.textSecondary }}>{player.phone}</span>
                        )}
                      </div>
                    ))}
                </div>

                <div className="openplay-card-ref" style={{ color: COLORS.inkFaint }}>Ref: {join.confirmationId}</div>
              </div>

              {hasReceipt(join) && (
                <button
                  type="button"
                  className="openplay-receipt-btn"
                  style={{ borderColor: COLORS.line, color: COLORS.cyanDeep }}
                  disabled={loadingReceiptId === join.id}
                  onClick={() => handleViewReceipt(join)}
                >
                  {loadingReceiptId === join.id ? 'Loading…' : 'View Receipt'}
                </button>
              )}

              {activeTab === 'pending' && (
                <div className="openplay-card-actions">
                  <button
                    type="button"
                    className="openplay-btn openplay-btn-confirm"
                    style={{ background: COLORS.success }}
                    disabled={actioningId === join.id}
                    onClick={() => handleAction(join.id, 'confirmed')}
                  >
                    {actioningId === join.id ? '…' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    className="openplay-btn openplay-btn-decline"
                    style={{ background: COLORS.danger }}
                    disabled={actioningId === join.id}
                    onClick={() => handleAction(join.id, 'declined')}
                  >
                    {actioningId === join.id ? '…' : 'Decline'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {fullscreenImage && (
        <div className="openplay-lightbox" onClick={() => setFullscreenImage(null)}>
          <img src={fullscreenImage} alt="Payment receipt" />
        </div>
      )}
    </div>
  );
}

export default AdminOpenPlay;
